import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import recallsService from '../../services/recalls.service';

const DB_FIELDS = [
    { value: 'FIRST_NAME', label: 'First Name' },
    { value: 'LAST_NAME', label: 'Last Name' },
    { value: 'PREFERRED_NAME', label: 'Preferred Name' },
    { value: 'DATE_OF_BIRTH', label: 'Date of Birth' },
    { value: 'PHONE_NUMBER', label: 'Phone Number' },
    { value: 'CELL_NUMBER', label: 'Cell Number' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'STREET', label: 'Street' },
    { value: 'CITY', label: 'City' },
    { value: 'POSTAL_CODE', label: 'Postal Code' },
    { value: 'PMS_ID', label: 'PMS ID' },
    { value: 'NUMBER', label: 'Patient Number' },
];

const emptyMapping = () => ({ csvColumn: '', dbField: 'FIRST_NAME' });
const emptyGroup = () => ({ mappings: [emptyMapping()] });

const parseCsvHeaderLine = (text) => {
    const firstLine = (text.split(/\r?\n/).find((line) => line.trim()) || '').replace(/^\uFEFF/, '');
    if (!firstLine) return [];

    const headers = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < firstLine.length; i++) {
        const c = firstLine[i];
        if (inQuotes) {
            if (c === '"') {
                if (i + 1 < firstLine.length && firstLine[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                current += c;
            }
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === ',') {
            headers.push(current.trim());
            current = '';
        } else {
            current += c;
        }
    }
    headers.push(current.trim());
    return headers.filter(Boolean);
};

const STATUS_BADGE = {
    PENDING: 'text-bg-secondary',
    RUNNING: 'text-bg-primary',
    COMPLETED: 'text-bg-success',
    FAILED: 'text-bg-danger',
};

function RecallProcessor({ recallId }) {
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);

    const [csvFile, setCsvFile] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [groups, setGroups] = useState([emptyGroup()]);
    const [job, setJob] = useState(null);
    const [starting, setStarting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [copying, setCopying] = useState(false);
    const [loadingLatest, setLoadingLatest] = useState(true);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const startPolling = useCallback(
        (jobId) => {
            stopPolling();
            pollRef.current = setInterval(() => {
                recallsService
                    .getRecallMatchJob(recallId, jobId)
                    .then((res) => {
                        setJob(res.data);
                        if (res.data.status === 'COMPLETED' || res.data.status === 'FAILED') {
                            stopPolling();
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            }, 3000);
        },
        [recallId, stopPolling]
    );

    useEffect(() => {
        setLoadingLatest(true);
        recallsService
            .getLatestRecallMatchJob(recallId)
            .then((res) => {
                setJob(res.data);
                if (res.data.status === 'PENDING' || res.data.status === 'RUNNING') {
                    startPolling(res.data.id);
                }
            })
            .catch(() => {
                setJob(null);
            })
            .finally(() => setLoadingLatest(false));

        return () => stopPolling();
    }, [recallId, startPolling, stopPolling]);

    const handleCsvSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please select a CSV file.');
            return;
        }

        try {
            const text = await file.slice(0, 64 * 1024).text();
            const headers = parseCsvHeaderLine(text);
            if (!headers.length) {
                toast.error('Could not read CSV headers.');
                return;
            }
            setCsvFile(file);
            setCsvHeaders(headers);
            setGroups([
                {
                    mappings: [{ csvColumn: headers[0], dbField: 'FIRST_NAME' }],
                },
            ]);
        } catch (err) {
            console.error(err);
            toast.error('Failed to read CSV file.');
        }
    };

    const updateMapping = (groupIndex, mappingIndex, patch) => {
        setGroups((prev) =>
            prev.map((group, gi) =>
                gi !== groupIndex
                    ? group
                    : {
                          ...group,
                          mappings: group.mappings.map((mapping, mi) =>
                              mi !== mappingIndex ? mapping : { ...mapping, ...patch }
                          ),
                      }
            )
        );
    };

    const addMapping = (groupIndex) => {
        setGroups((prev) =>
            prev.map((group, gi) =>
                gi !== groupIndex
                    ? group
                    : { ...group, mappings: [...group.mappings, emptyMapping()] }
            )
        );
    };

    const removeMapping = (groupIndex, mappingIndex) => {
        setGroups((prev) =>
            prev.map((group, gi) => {
                if (gi !== groupIndex) return group;
                if (group.mappings.length <= 1) return group;
                return {
                    ...group,
                    mappings: group.mappings.filter((_, mi) => mi !== mappingIndex),
                };
            })
        );
    };

    const addGroup = () => {
        setGroups((prev) => [...prev, emptyGroup()]);
    };

    const removeGroup = (groupIndex) => {
        setGroups((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== groupIndex)));
    };

    const validateGroups = () => {
        for (let g = 0; g < groups.length; g++) {
            for (let m = 0; m < groups[g].mappings.length; m++) {
                const mapping = groups[g].mappings[m];
                if (!mapping.csvColumn) {
                    toast.error(`Group ${g + 1}, condition ${m + 1}: select a CSV column.`);
                    return false;
                }
                if (!mapping.dbField) {
                    toast.error(`Group ${g + 1}, condition ${m + 1}: select a database field.`);
                    return false;
                }
            }
        }
        return true;
    };

    const handleStartMatch = async () => {
        if (!csvFile) {
            toast.error('Select a CSV file first.');
            return;
        }
        if (!validateGroups()) return;

        setStarting(true);
        try {
            const payload = {
                groups: groups.map((group) => ({
                    mappings: group.mappings.map((mapping) => ({
                        csvColumn: mapping.csvColumn,
                        dbField: mapping.dbField,
                    })),
                })),
            };
            const response = await recallsService.startRecallMatchJob(recallId, csvFile, payload);
            setJob(response.data);
            startPolling(response.data.id);
            toast.success('Match job started.');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to start match job.');
        } finally {
            setStarting(false);
        }
    };

    const handleDownloadResult = async () => {
        if (!job?.id) return;
        setDownloading(true);
        try {
            const response = await recallsService.downloadRecallMatchJobResult(recallId, job.id);
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `recall-match-${job.id}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            toast.error('Failed to download match result.');
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyIds = async () => {
        if (!job?.id) return;
        setCopying(true);
        try {
            const response = await recallsService.downloadRecallMatchJobResult(recallId, job.id);
            const text = await response.data.text();
            const ids = text
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter((line) => line && line.toLowerCase() !== 'patientid')
                .join('\n');
            await navigator.clipboard.writeText(ids);
            toast.success('Patient IDs copied to clipboard.');
        } catch (err) {
            console.error(err);
            toast.error('Failed to copy patient IDs.');
        } finally {
            setCopying(false);
        }
    };

    const isBusy = starting || job?.status === 'PENDING' || job?.status === 'RUNNING';

    return (
        <div className="notifications-panel recall-processor">
            <div className="notifications-panel__header">
                <i className="bi bi-funnel"></i>
                Recall Processor
            </div>
            <div className="notifications-panel__body d-grid gap-3">
                <div className="text-muted small">
                    Map CSV columns into AND groups (OR between groups). Matching runs as a background job for large files.
                    Download the patient ID CSV when complete, then upload it under File Management if needed.
                </div>

                <div>
                    <div className="text-muted small mb-2">Source CSV</div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv,text/csv"
                        className="d-none"
                        onChange={handleCsvSelect}
                        disabled={isBusy}
                    />
                    <div className="recall-processor__file-row">
                        <div className="recall-edit__file-name">
                            <i className="bi bi-filetype-csv text-primary"></i>
                            <span title={csvFile?.name || ''}>
                                {csvFile ? csvFile.name : 'No CSV selected'}
                            </span>
                        </div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <i className="bi bi-upload me-1"></i>
                            Select CSV
                        </Button>
                    </div>
                    {csvHeaders.length > 0 && (
                        <div className="text-muted small mt-2">
                            Detected {csvHeaders.length} columns
                        </div>
                    )}
                </div>

                <div className="d-grid gap-3">
                    {groups.map((group, groupIndex) => (
                        <div key={groupIndex} className="recall-processor__group">
                            <div className="recall-processor__group-header">
                                <span className="small fw-semibold">
                                    Group {groupIndex + 1}
                                    <span className="text-muted fw-normal"> — all conditions AND’d</span>
                                </span>
                                {groups.length > 1 && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        disabled={isBusy}
                                        onClick={() => removeGroup(groupIndex)}
                                    >
                                        Remove group
                                    </Button>
                                )}
                            </div>

                            {groupIndex > 0 && (
                                <div className="recall-processor__or-badge">OR</div>
                            )}

                            <div className="d-grid gap-2">
                                {group.mappings.map((mapping, mappingIndex) => (
                                    <div key={mappingIndex} className="recall-processor__mapping-row">
                                        {mappingIndex > 0 && (
                                            <div className="recall-processor__and-badge">AND</div>
                                        )}
                                        <select
                                            className="form-select form-select-sm"
                                            value={mapping.csvColumn}
                                            disabled={isBusy || csvHeaders.length === 0}
                                            onChange={(e) =>
                                                updateMapping(groupIndex, mappingIndex, {
                                                    csvColumn: e.target.value,
                                                })
                                            }
                                        >
                                            <option value="">CSV column…</option>
                                            {csvHeaders.map((header) => (
                                                <option key={header} value={header}>
                                                    {header}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted small">→</span>
                                        <select
                                            className="form-select form-select-sm"
                                            value={mapping.dbField}
                                            disabled={isBusy}
                                            onChange={(e) =>
                                                updateMapping(groupIndex, mappingIndex, {
                                                    dbField: e.target.value,
                                                })
                                            }
                                        >
                                            {DB_FIELDS.map((field) => (
                                                <option key={field.value} value={field.value}>
                                                    {field.label}
                                                </option>
                                            ))}
                                        </select>
                                        {group.mappings.length > 1 && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={isBusy}
                                                onClick={() => removeMapping(groupIndex, mappingIndex)}
                                                aria-label="Remove condition"
                                            >
                                                <i className="bi bi-x"></i>
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="mt-2 align-self-start"
                                disabled={isBusy || csvHeaders.length === 0}
                                onClick={() => addMapping(groupIndex)}
                            >
                                <i className="bi bi-plus me-1"></i>
                                Add AND condition
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={isBusy || csvHeaders.length === 0}
                        onClick={addGroup}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add OR group
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={isBusy || !csvFile}
                        onClick={handleStartMatch}
                    >
                        {starting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Starting…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-play-circle me-1"></i>
                                Start match
                            </>
                        )}
                    </Button>
                </div>

                {loadingLatest ? (
                    <div className="text-muted small">
                        <Spinner animation="border" size="sm" className="me-1" />
                        Loading latest job…
                    </div>
                ) : job ? (
                    <div className="recall-processor__job">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <span className="small fw-semibold">Latest job</span>
                            <span className={`badge ${STATUS_BADGE[job.status] || 'text-bg-secondary'}`}>
                                {job.status}
                            </span>
                            <span className="font-monospace text-muted small">{job.id}</span>
                        </div>

                        {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                            <Alert variant="info" className="py-2 px-3 mb-2 border-0 small">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Matching in progress. This page polls every few seconds.
                            </Alert>
                        )}

                        {job.status === 'FAILED' && (
                            <Alert variant="danger" className="py-2 px-3 mb-2 border-0 small">
                                {job.errorMessage || 'Match job failed.'}
                            </Alert>
                        )}

                        {job.status === 'COMPLETED' && (
                            <>
                                <div className="recall-processor__stats small text-muted mb-2">
                                    <span>Rows: {job.totalCsvRows ?? 0}</span>
                                    <span>Matched rows: {job.matchedRowCount ?? 0}</span>
                                    <span>Unmatched: {job.unmatchedRowCount ?? 0}</span>
                                    <span>Unique patients: {job.matchedPatientCount ?? 0}</span>
                                    <span>Duplicate skips: {job.duplicateSkippedCount ?? 0}</span>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={downloading}
                                        onClick={handleDownloadResult}
                                    >
                                        {downloading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-1" />
                                                Downloading…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-download me-1"></i>
                                                Download patientId CSV
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={copying}
                                        onClick={handleCopyIds}
                                    >
                                        {copying ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-1" />
                                                Copying…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-clipboard me-1"></i>
                                                Copy IDs
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="text-muted small">No match jobs yet for this request.</div>
                )}
            </div>
        </div>
    );
}

export default RecallProcessor;
